import { useCallback, useEffect, useState } from "react";
import PortalLayout from "../../components/PortalLayout";
import { api, formatApiErrorDetail, formatDate } from "../../lib/api";

export default function AdminStatementsPage() {
  const [users, setUsers] = useState([]);
  const [statements, setStatements] = useState([]);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    user_id: "",
    deposit_id: "",
    period: "",
    kind: "quarterly",
    file_url: "",
    label: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(
    () =>
      Promise.all([api.get("/admin/users"), api.get("/admin/statements")])
        .then(([u, s]) => {
          setUsers(u.data.filter((x) => x.role === "client"));
          setStatements(s.data);
        })
        .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message)),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const body = { ...form };
      if (!body.deposit_id) delete body.deposit_id;
      await api.post("/admin/statements", body);
      setForm({ ...form, period: "", file_url: "", label: "", deposit_id: "" });
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PortalLayout>
      <section className="max-w-[1280px] mx-auto px-6 sm:px-8 pt-14 pb-8" data-testid="admin-statements-page">
        <div className="br-fade-up">
          <div className="br-overline">Estratti</div>
          <h1 className="font-serif-display text-[clamp(2.4rem,4.5vw,3.6rem)] mt-3">
            Pubblica <em>estratti</em>.
          </h1>
          <p className="text-[1.05rem] text-[color:var(--br-text)] mt-3 max-w-[640px]">
            Carica un file PDF su storage esterno (S3, Drive, Dropbox) e incolla l&apos;URL pubblico/firmato qui sotto. Il cliente lo vedrà subito nel suo portale.
          </p>
        </div>

        <div className="mt-10 grid lg:grid-cols-5 gap-8 br-fade-up br-delay-1">
          <form onSubmit={submit} className="br-card p-7 space-y-4 lg:col-span-2" data-testid="admin-statement-form">
            <h2 className="font-serif-display text-[1.6rem]">Nuovo estratto</h2>
            <label className="block">
              <span className="br-overline block mb-2">Cliente</span>
              <select
                className="br-input"
                style={{ background: "var(--br-bg-2)" }}
                value={form.user_id}
                onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                required
                data-testid="statement-form-user"
              >
                <option value="">— seleziona —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} · {u.email}
                  </option>
                ))}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label>
                <span className="br-overline block mb-2">Periodo</span>
                <input
                  className="br-input"
                  value={form.period}
                  onChange={(e) => setForm({ ...form, period: e.target.value })}
                  placeholder="2026-Q1"
                  required
                  data-testid="statement-form-period"
                />
              </label>
              <label>
                <span className="br-overline block mb-2">Tipo</span>
                <select
                  className="br-input"
                  style={{ background: "var(--br-bg-2)" }}
                  value={form.kind}
                  onChange={(e) => setForm({ ...form, kind: e.target.value })}
                  data-testid="statement-form-kind"
                >
                  <option value="quarterly">Trimestrale</option>
                  <option value="monthly">Mensile</option>
                  <option value="annual">Annuale</option>
                  <option value="coupon">Cedola</option>
                </select>
              </label>
            </div>
            <label className="block">
              <span className="br-overline block mb-2">Etichetta</span>
              <input
                className="br-input"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Estratto trimestrale Q1 2026"
                data-testid="statement-form-label"
              />
            </label>
            <label className="block">
              <span className="br-overline block mb-2">URL file PDF</span>
              <input
                className="br-input"
                value={form.file_url}
                onChange={(e) => setForm({ ...form, file_url: e.target.value })}
                placeholder="https://…/estratto.pdf"
                required
                data-testid="statement-form-url"
              />
            </label>

            {error && (
              <div className="text-[0.85rem] py-3 px-4 border" style={{ borderColor: "var(--br-red)", color: "var(--br-red)", background: "rgba(179,107,107,0.06)" }} data-testid="statement-form-error">
                {error}
              </div>
            )}
            <button type="submit" className="br-btn-primary w-full" disabled={submitting} data-testid="statement-form-submit">
              {submitting ? "Pubblicazione…" : "Pubblica estratto"}
            </button>
          </form>

          <div className="lg:col-span-3 br-card overflow-x-auto" data-testid="admin-statements-table">
            <table className="w-full text-left">
              <thead>
                <tr style={{ borderBottom: "1px solid var(--br-line)" }}>
                  <Th>Cliente</Th>
                  <Th>Periodo</Th>
                  <Th>Tipo</Th>
                  <Th>Etichetta</Th>
                  <Th>Pubblicato</Th>
                  <Th align="right">File</Th>
                </tr>
              </thead>
              <tbody>
                {statements.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-5 py-8 text-center text-[color:var(--br-text-dim)]">
                      Nessun estratto pubblicato.
                    </td>
                  </tr>
                ) : (
                  statements.map((s) => {
                    const user = users.find((u) => u.id === s.user_id);
                    return (
                      <tr key={s.id} className="border-b" style={{ borderColor: "var(--br-line)" }} data-testid={`statement-row-${s.id}`}>
                        <Td className="text-[color:var(--br-parchment)]">{user?.name || s.user_id.slice(-6)}</Td>
                        <Td className="font-mono-fin" style={{ color: "var(--br-bronze)" }}>{s.period}</Td>
                        <Td className="font-mono-fin uppercase text-[0.66rem] tracking-[0.18em]">{s.kind}</Td>
                        <Td className="font-serif-display italic">{s.label}</Td>
                        <Td className="font-mono-fin text-[color:var(--br-text-dim)]">{formatDate(s.created_at)}</Td>
                        <Td align="right">
                          <a
                            href={s.file_url}
                            target="_blank"
                            rel="noreferrer"
                            className="font-mono-fin text-[0.68rem] tracking-[0.16em] uppercase"
                            style={{ color: "var(--br-bronze-soft)" }}
                          >
                            Apri →
                          </a>
                        </Td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </PortalLayout>
  );
}

function Th({ children, align = "left" }) {
  return <th className="px-5 py-4 font-mono-fin text-[0.62rem] tracking-[0.22em] uppercase font-medium" style={{ color: "var(--br-text-dim)", textAlign: align }}>{children}</th>;
}
function Td({ children, align = "left", className = "", style }) {
  return <td className={`px-5 py-4 text-[0.9rem] ${className}`} style={{ textAlign: align, ...style }}>{children}</td>;
}
